import React from "react";
import ReactDOM from "react-dom/client";
import type { AccountInfo } from "@azure/msal-browser";
import "./styles.css";
import { DevBanner } from "./components/DevBanner";
import { ImpersonationBanner } from "./components/ImpersonationBanner";

/**
 * The bare root (ruthless.software) is the public marketing site; the app lives at /am and the
 * platform console at /platformadmin. We split on the pathname at boot — the landing renders on its
 * own, loading none of the app's MSAL/session machinery, so the marketing page stays lean. This is
 * the same sanctioned use of the URL path as App.tsx's /am-vs-/platformadmin split (see CLAUDE.md);
 * it resolves which top-level surface loads, not navigation within the app.
 */
const isLandingRoute = window.location.pathname.replace(/\/+$/, "") === "";

async function bootstrap() {
  const root = ReactDOM.createRoot(document.getElementById("root")!);

  if (isLandingRoute) {
    const { Landing } = await import("./screens/Landing");
    root.render(
      <React.StrictMode>
        <DevBanner />
        <Landing />
      </React.StrictMode>,
    );
    return;
  }

  // MSAL and the whole app load only off the root, so the marketing page never pulls them in.
  const [{ PublicClientApplication, EventType }, { MsalProvider }, { msalConfig }, { default: App }, { ToastHost }] =
    await Promise.all([
      import("@azure/msal-browser"),
      import("@azure/msal-react"),
      import("./authConfig"),
      import("./App"),
      import("./ToastHost"),
    ]);

  const msalInstance = new PublicClientApplication(msalConfig);
  await msalInstance.initialize();

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) msalInstance.setActiveAccount(accounts[0]);

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as { account?: AccountInfo };
      if (payload.account) msalInstance.setActiveAccount(payload.account);
    }
  });

  await msalInstance.handleRedirectPromise();

  root.render(
    <React.StrictMode>
      <DevBanner />
      <ImpersonationBanner />
      <MsalProvider instance={msalInstance}>
        <App />
        <ToastHost />
      </MsalProvider>
    </React.StrictMode>,
  );
}

void bootstrap();
